/**
 * Compact comparison editor for ONE override row, reading as a sentence:
 * compares ( this row | the TOTAL )’s [metric ▼] over [window ▼]. The % is
 * not offered here (it stays behind SHOW_COMPARE_PERCENT like everywhere
 * else); a stored percent is preserved untouched by the patches. Switching
 * the metric to a drop-scoped field strips a stored `against:"total"` —
 * the backend rejects that pair.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC } from 'react';
import type { TagCompareTo } from '@trafficpulse/shared';

import { LIST_METRIC_FIELDS } from '@/pages/admin/ListTagRulesPage/constants';

import AgainstToggle from './AgainstToggle';
import CompareCustomBounds from './CompareCustomBounds';
import { isDropScoped, patchComparedField } from './CompareTargetGroup';
import { compareWindowOptions, windowValueOf, withWindow } from '../compare-window';
import { useCompareWindowExclusions } from '../window-exclusions';

export interface OverrideCompareControlsProps {
  ct: TagCompareTo;
  onChange: (next: TagCompareTo) => void;
  fields?: ReadonlyArray<{ key: string; label: string }>;
}

const SELECT =
  'h-8 min-w-0 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export const OverrideCompareControls: FC<OverrideCompareControlsProps> = ({
  ct,
  onChange,
  fields = LIST_METRIC_FIELDS,
}) => {
  const excluded = useCompareWindowExclusions();
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="shrink-0 text-[11px] text-muted-foreground">compares</span>
        <AgainstToggle
          size="sm"
          labels={{ self: 'this row', total: 'the TOTAL' }}
          total={ct.against === 'total'}
          totalUnavailable={isDropScoped(ct.field)}
          onChange={(total) =>
            onChange({ ...ct, against: total ? 'total' : undefined })
          }
        />
        <span className="shrink-0 text-[11px] text-muted-foreground">’s</span>
        <select
          value={ct.field}
          onChange={(e) => onChange(patchComparedField(ct, e.target.value))}
          aria-label="Override compared metric"
          className={`${SELECT} w-36`}
        >
          {fields.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        <span className="shrink-0 text-[11px] text-muted-foreground">over</span>
        <select
          value={windowValueOf(ct)}
          onChange={(e) => onChange(withWindow(ct, e.target.value))}
          aria-label="Override compare window"
          className={`${SELECT} w-40`}
        >
          {compareWindowOptions(excluded).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {ct.dateRangePreset === 'custom' && (
        <CompareCustomBounds ct={ct} onPatch={onChange} />
      )}
    </div>
  );
};

export default OverrideCompareControls;
